import type { Socket } from 'socket.io-client';

import { useQueryClient } from '@tanstack/react-query';
import { captureException } from '@sentry/react';
import { io } from 'socket.io-client';
import React from 'react';

import { isSessionOverError } from '../context/authErrorToast';
import { getSignOutReason } from '../api/http/signOutReason';
import { SessionMovedError } from '../api/sessionCookie';
import { useTenantContext } from '../context/TenantProvider';
import { useAuthContext } from '../context/AuthProvider';
import { describeErrorForTag } from '../utils/describeErrorForTag';
import { useOrganizationContext } from '../context/OrganizationProvider/OrganizationProvider';
import { useSocketStore } from '../store/useSocketStore';
import { sessionCookie, socketBaseUrl } from '../api/axios';
import {
  catchUpMissedSocketEvents,
  createSocketCatchUpScheduler,
} from './socketCatchUp';

/**
 * PR-2 §4.8. The handshake authenticates with the `auth` cookie the browser attaches
 * (`withCredentials`); no ID token is read. The server refuses a dead cookie AFTER it has
 * already answered CONNECT, so a refusal is `connect` followed by
 * `disconnect('io server disconnect')` — which socket.io-client never retries on its own.
 * ONE such disconnect per instance is answered by one guard recovery (the same mint the
 * HTTP layer uses) and one `connect()`; a second is left alone, because `connect` cannot
 * tell an accepted handshake from one about to be refused.
 *
 * The budget buys ONE reconnect ON A FRESH COOKIE, and only that reconnect spends it
 * (auth-11 P2-3). A recovery the guard DECLINED — an active failure cooldown rethrows the
 * cached error without minting at all — or one that failed for connectivity produced no
 * such cookie and spends nothing: the instance parks on the guard's `onConfirmed` and
 * reconnects once when the HTTP layer repairs the session. Spending it there left realtime
 * dead until a reload, because nothing in the dependency list moves on an ordinary mint.
 * The instance is recreated on account, tenant or session-revision change, which is what
 * resets the budget — and on NOTHING else.
 *
 * `signOutAsync` is deliberately not a dependency: its identity follows
 * `useNavigate`, which react-router re-memoizes on every pathname, so depending on it tore
 * the socket down and opened a new one on every page change. Events arriving in that gap
 * are lost — the five consumers of `useAppSockets` key their listeners on the instance —
 * and a fresh instance would hand a refused cookie a new recovery on every navigation.
 * `AuthProvider` keeps a `signOutAsyncRef` for the same reason; this is the socket's.
 */
/**
 * The instance's one recovery is spent and the gateway dropped it again.
 *
 * A named class purely so the Sentry tag can say so: `describeErrorForTag` distils an
 * `Error` down to its `name`, and a bare `new Error(...)` made this — the budget running
 * out, which needs a reload or a new session to clear — indistinguishable from every other
 * failure that reaches the same reporter under the tag `Error`.
 */
class SocketRecoveryExhaustedError extends Error {
  constructor() {
    super('The socket instance has already spent its one recovery');
    this.name = 'SocketRecoveryExhausted';
  }
}

/**
 * Where the instance's one reconnect stands.
 *
 * `idle` — nothing owed, a server disconnect is answered with a recovery.
 * `recovering` — an attempt is in flight; further server disconnects are the same episode.
 * `parked` — no fresh cookie exists yet, so there is nothing to reconnect ON. Waiting for
 * the guard to say a mint was confirmed; the budget is untouched.
 * `spent` — the reconnect happened. The next server disconnect is exhaustion.
 */
type ReconnectBudget = 'idle' | 'parked' | 'recovering' | 'spent';

export function useSocketInitializer() {
  const {
    currentUser,
    isCredentialTransitionActive,
    refetchCurrentUser,
    sessionRevision,
    signOutAsync,
  } = useAuthContext();
  const { tenant } = useTenantContext();
  const { clearOrganization, currentOrganization } = useOrganizationContext();
  const queryClient = useQueryClient();
  const { setSocket } = useSocketStore();
  // Read through a ref, never through the dependency list. See the note above.
  const signOutRef = React.useRef(signOutAsync);
  signOutRef.current = signOutAsync;
  // The catch-up's collaborators, through a ref for the same reason: an organization
  // switch or a new `/auth/me` must not tear the socket down, and the catch-up must still
  // act on the organization selected when it runs, not when the instance was opened.
  const catchUpRef = React.useRef({
    clearOrganization,
    getCurrentOrganizationId: () => currentOrganization?.id,
    queryClient,
    refetchCurrentUser,
  });
  catchUpRef.current = {
    clearOrganization,
    getCurrentOrganizationId: () => currentOrganization?.id,
    queryClient,
    refetchCurrentUser,
  };

  React.useEffect(() => {
    if (!currentUser?.id || isCredentialTransitionActive) {
      setSocket(null);
      return;
    }
    let disposed = false;
    let budget: ReconnectBudget = 'idle';
    /** Ends the subscription this instance is parked on, `null` when it is not parked. */
    let unpark: (() => void) | null = null;
    let reported = false;
    const owner = sessionCookie.getStamp();
    const isLive = () => !disposed && sessionCookie.isCurrent(owner);
    // auth-11 P2-1: the socket shares axios's host, and must. Its only credential is the
    // `auth` cookie, which `auth-cookie.ts` sets without a `Domain` and the browser
    // therefore keeps host-only — it exists on the host that answered `/auth/me` and
    // nowhere else. Deployed that is this page's own origin (axios goes through nginx at
    // `/api`), so a handshake aimed at the backend host directly would carry nothing and
    // be disconnected, spending the instance's one recovery on a refusal no mint can fix.
    const instance: Socket = io(socketBaseUrl, {
      auth: { tenantId: tenant?.id },
      autoConnect: false,
      path: '/api/socket.io',
      // Both halves are what nginx's `/api/socket.io/` block is written against: the
      // default trailing slash the client adds to `path` is the prefix that block matches,
      // and websocket-only means every request it sees is an upgrade, which is why that
      // block may set `Connection: upgrade` unconditionally (auth-11 P2-1).
      transports: ['websocket'],
      withCredentials: true,
    });

    const report = (error: unknown) => {
      if (!isLive() || reported) return;
      reported = true;
      captureException(
        new Error('Socket handshake refused; realtime stayed offline'),
        {
          level: 'warning',
          tags: { socketAuthRefused: describeErrorForTag(error) },
        },
      );
    };
    /**
     * Wait for the HTTP layer to repair the session, then try `resume` again. The guard is
     * the only thing that knows when that happens: an ordinary mint moves nothing this
     * effect depends on, so without the notice the instance would sit here until a reload.
     *
     * `seen` is the generation this attempt STARTED from. The subscription is only taken
     * once the rejection has been delivered, and a mint confirmed inside that window was
     * announced to nobody — so a higher generation is the same news after the fact, and
     * waiting for a notice that has already been and gone is how the park becomes the very
     * dead end it exists to prevent.
     */
    const park = (resume: () => Promise<void>, seen: number) => {
      budget = 'parked';
      unpark?.(); // one subscription per instance, however often it parks
      unpark = null;
      if (!isLive()) return;
      if (sessionCookie.getStamp().generation > seen) {
        budget = 'recovering';
        void resume();
        return;
      }
      unpark = sessionCookie.onConfirmed(() => {
        unpark?.();
        unpark = null;
        budget = 'recovering';
        void resume();
      });
    };

    const handleFailure = (
      error: unknown,
      resume: () => Promise<void>,
      seen: number,
    ) => {
      if (!isLive()) return;
      // The effect is recreated by its own dependencies; the instance that replaces this
      // one opens on the session that moved under it. Back to `idle` all the same: no
      // state this instance can still be in may be one it can never leave.
      if (error instanceof SessionMovedError) {
        budget = 'idle';
        return;
      }
      if (isSessionOverError(error)) {
        report(error);
        // The classifier, not the sign-out predicate: this hook may be the ONLY observer
        // of the refusal — an idle tab's HTTP layer is silent and the SDK fires nothing
        // for a cookie the gateway rejected — so what the login page is told about it is
        // decided here. `getSignOutReason` answers `null` for most of what
        // `isSessionOverError` accepts, which is the point: the sign-out is unconditional,
        // the sentence is not.
        void signOutRef.current({
          reason: getSignOutReason(error),
          source: 'terminal',
        });
        return;
      }
      // Nothing was minted, so nothing was refused: the budget is untouched and the guard
      // has already reported this outage once. A second warning for it would say nothing
      // the first did not — this reporter is for a spent budget and a refused identity.
      park(resume, seen);
    };

    /**
     * Connect on the cookie the guard has just confirmed. `isReconnect` is the whole
     * difference the budget cares about: opening the instance for the first time is not
     * the reconnect it pays for, and publishing it is what that first connect owes.
     */
    const connectOnFreshCookie = async (isReconnect: boolean) => {
      const resume = () => connectOnFreshCookie(isReconnect);
      if (!isLive()) return;
      const seen = sessionCookie.getStamp().generation;
      try {
        // A follow-on mint may be in flight behind the confirmation; the barrier is what
        // keeps the handshake off a cookie that is halfway rewritten.
        await sessionCookie.join();
      } catch (error) {
        handleFailure(error, resume, seen);
        return;
      }
      if (!isLive()) return;
      budget = isReconnect ? 'spent' : 'idle';
      instance.connect();
      if (!isReconnect) setSocket(instance);
    };

    const onDisconnect = (reason: string) => {
      if (reason !== 'io server disconnect' || !isLive()) return;
      // One attempt at a time: the gateway can drop the same instance repeatedly while the
      // mint that would fix it is still on the wire, and that is one episode, not three.
      if (budget === 'recovering' || budget === 'parked') return;
      if (budget === 'spent') {
        report(new SocketRecoveryExhaustedError());
        return;
      }
      const rejected = sessionCookie.getStamp(); // current generation at DISCONNECT, not initial connect
      budget = 'recovering';
      // The generation this attempt starts from, for the park below.
      const seen = rejected.generation;
      void (async () => {
        try {
          const verdict = await sessionCookie.recover(rejected);
          if (!isLive()) return;
          // The guard has left this session behind; so has the effect, in a moment. The
          // budget goes back all the same — `isLive()` is what gates every path, and a
          // state nothing can leave is a dead end waiting for the one case that outlives
          // its effect.
          if (verdict === 'stale-session') {
            budget = 'idle';
            return;
          }
          await sessionCookie.join();
          if (!isLive()) return;
          // Set BEFORE connecting: a synchronous refusal of `connect` is the second
          // episode, and this reconnect is what the budget bought.
          budget = 'spent';
          instance.connect();
        } catch (error) {
          handleFailure(error, () => connectOnFreshCookie(true), seen);
        }
      })();
    };
    instance.on('disconnect', onDisconnect);

    /**
     * The catch-up owner. Socket.io fires `connect` for the first handshake and again for
     * every reconnect — its own automatic transport reconnect and the explicit one the
     * recovery above makes. Only a RE-connect can have missed a push, so the first is
     * skipped. Nothing here touches the budget: `connect` proves nothing about a cookie
     * the gateway may refuse a moment later (see the note at the top).
     */
    let hasConnected = false;
    // Coalesced: a burst of reconnects is one catch-up (see the scheduler). Its
    // collaborators are read through the ref WHEN it runs, seconds after the `connect` —
    // the organization it reconciles is the one selected then, and on both sides of its
    // `/auth/me` await.
    const catchUp = createSocketCatchUpScheduler(() =>
      catchUpMissedSocketEvents({
        clearOrganization: () => catchUpRef.current.clearOrganization(),
        getCurrentOrganizationId: () =>
          catchUpRef.current.getCurrentOrganizationId(),
        isLive,
        queryClient: catchUpRef.current.queryClient,
        refetchCurrentUser: () => catchUpRef.current.refetchCurrentUser(),
      }),
    );
    const onConnect = () => {
      if (!isLive()) return;
      if (!hasConnected) {
        hasConnected = true;
        return;
      }
      catchUp.request();
    };
    // Registered before anything connects, like the disconnect listener.
    instance.on('connect', onConnect);

    // Read before the barrier is awaited, for the same reason the recovery reads it: a
    // mint confirmed while this one is failing is a notice the park would never hear.
    const seenAtOpen = sessionCookie.getStamp().generation;
    void sessionCookie.join().then(
      () => {
        if (!isLive()) return;
        instance.connect();
        setSocket(instance);
      },
      (error: unknown) =>
        handleFailure(error, () => connectOnFreshCookie(false), seenAtOpen),
    );

    return () => {
      disposed = true;
      // A confirmation that lands after this must not join, connect or publish through an
      // instance that is already disconnected.
      unpark?.();
      unpark = null;
      instance.off('disconnect', onDisconnect);
      instance.off('connect', onConnect);
      catchUp.dispose();
      instance.disconnect();
      setSocket(null);
    };
  }, [
    currentUser?.id,
    isCredentialTransitionActive,
    sessionRevision,
    setSocket,
    tenant?.id,
  ]);
}
